import type { DetailItem, WebsiteContent } from '../types/content';
import {
  findHighLevelIssue,
  getItem,
  resolvePrimaryIssuePath,
} from './contentUtils';

export const CLARITY_TOPIC_ISSUE_IDS = [
  'anti_discrimination_civil_rights',
  'healthcare_coverage',
  'abuse_of_power',
  'government_accountability_transparency',
] as const;

export type ClarityTopicIssueId = (typeof CLARITY_TOPIC_ISSUE_IDS)[number];

export interface ClarityTopicLink {
  item: DetailItem;
  issueId: ClarityTopicIssueId;
  issueTitle: string;
}

/** Topics must have core copy and a resolvable primary issue path for canonical URLs. */
export function isCompleteTopicItem(data: WebsiteContent, item: DetailItem): boolean {
  if (!item.title?.trim() || !item.slug?.trim() || !item.body?.trim()) {
    return false;
  }
  if (!item.primaryIssueId?.trim() || !item.primarySpecificIssueId?.trim()) {
    return false;
  }
  return Boolean(resolvePrimaryIssuePath(data, item));
}

export function getValidTopicsForIssue(
  data: WebsiteContent,
  issueId: ClarityTopicIssueId,
): DetailItem[] {
  const itemIds = data.index.byHighLevelIssue[issueId] ?? [];
  return itemIds
    .map((id) => getItem(data, id))
    .filter((item): item is DetailItem => Boolean(item))
    .filter((item) => isCompleteTopicItem(data, item));
}

/** Calendar seed from year, month, and day so picks are stable for the day and change daily. */
export function getDateSeed(date = new Date()): number {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return year * 10_000 + month * 100 + day;
}

function pickBySeed<T>(items: T[], seed: number, slot: number): T | undefined {
  if (items.length === 0) return undefined;
  return items[(seed + slot) % items.length];
}

function rotateIssueIds(seed: number): ClarityTopicIssueId[] {
  const start = seed % CLARITY_TOPIC_ISSUE_IDS.length;
  return Array.from(
    { length: CLARITY_TOPIC_ISSUE_IDS.length },
    (_, index) => CLARITY_TOPIC_ISSUE_IDS[(start + index) % CLARITY_TOPIC_ISSUE_IDS.length],
  );
}

/** Pick up to `count` topics from distinct issue areas; selection rotates by calendar day. */
export function pickDailyClarityTopics(
  data: WebsiteContent,
  count = 2,
  date = new Date(),
): ClarityTopicLink[] {
  const seed = getDateSeed(date);
  const issueOrder = rotateIssueIds(seed);
  const picks: ClarityTopicLink[] = [];
  const usedItemIds = new Set<string>();

  for (const issueId of issueOrder) {
    if (picks.length >= count) break;

    const highLevel = findHighLevelIssue(data, issueId);
    if (!highLevel) continue;

    const candidates = getValidTopicsForIssue(data, issueId)
      .filter((item) => !usedItemIds.has(item.id))
      .sort((a, b) => a.id.localeCompare(b.id));

    const item = pickBySeed(candidates, seed, picks.length * 17 + issueId.length);
    if (!item) continue;

    usedItemIds.add(item.id);
    picks.push({
      item,
      issueId,
      issueTitle: highLevel.title,
    });
  }

  return picks;
}
